import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Handshake, ChevronDown, ChevronUp, Plus, Send, Clock, CheckCircle, XCircle, Tag, Package } from 'lucide-react';
import ImageLightbox from './ImageLightbox';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  submitted:         { label: 'SUBMITTED · PENDING REVIEW', color: 'text-yellow-400', bg: 'bg-yellow-500/20', icon: Clock },
  needs_review:      { label: 'ACTION NEEDED',              color: 'text-orange-400', bg: 'bg-orange-500/20', icon: Clock },
  accepted:          { label: 'ACCEPTED',                   color: 'text-blue-400',   bg: 'bg-blue-500/20',   icon: CheckCircle },
  awaiting_shipment: { label: 'ACCEPTED · AWAITING ITEM',   color: 'text-blue-400',   bg: 'bg-blue-500/20',   icon: Package },
  in_transit:        { label: 'IN TRANSIT',                 color: 'text-blue-300',   bg: 'bg-blue-500/15',   icon: Package },
  received:          { label: 'RECEIVED · READY TO LIST',   color: 'text-purple-300', bg: 'bg-purple-500/20', icon: CheckCircle },
  listed:            { label: 'LISTED',                     color: 'text-teal-400',   bg: 'bg-teal-500/20',   icon: Tag },
  sold:              { label: 'SOLD',                       color: 'text-purple-400', bg: 'bg-purple-500/20', icon: CheckCircle },
  rejected:          { label: 'DECLINED',                   color: 'text-red-400',    bg: 'bg-red-500/20',    icon: XCircle },
  returned:          { label: 'RETURNED',                   color: 'text-zinc-400',   bg: 'bg-zinc-500/20',   icon: Package },
  expired:           { label: 'EXPIRED',                    color: 'text-zinc-400',   bg: 'bg-zinc-500/20',   icon: Clock },
};

const getImageUrl = (img) => {
  if (!img) return '';
  if (img.startsWith('http')) return img;
  return `${API_URL}${img}`;
};

export default function MyConsignmentsSection() {
  const [data, setData]   = useState({ items: [], wallet: null });
  const [loading, setLoading]   = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [lightbox, setLightbox] = useState(null); // { images, initialIndex }

  useEffect(() => {
    const load = async () => {
      try {
        const token = localStorage.getItem('blkmrkt_token') || sessionStorage.getItem('blkmrkt_token');
        if (!token) { setLoading(false); return; }
        const res = await fetch(`${API_URL}/api/consignment/my`, { headers: { Authorization: `Bearer ${token}` } });
        if (res.ok) setData(await res.json());
      } catch { /* silent */ }
      finally { setLoading(false); }
    };
    load();
  }, []);

  const items = data.items || [];
  const wallet = data.wallet || { balance: 0, total_earned: 0 };

  const renderStatusBadge = (status) => {
    const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.submitted;
    const Icon = cfg.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-none text-xs font-mono ${cfg.bg} ${cfg.color}`}>
        <Icon size={12} />
        {cfg.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="border border-zinc-800 rounded-none p-4">
        <h3 className="font-mono text-sm text-teal-500 mb-3">MY CONSIGNMENTS</h3>
        <p className="text-zinc-500 text-xs font-mono">LOADING...</p>
      </div>
    );
  }

  return (
    <div className="border border-zinc-800 rounded-none p-4">
      <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
        <h3 className="font-mono text-sm text-teal-500">MY CONSIGNMENTS</h3>
        <div className="flex items-center gap-2">
          {wallet.balance > 0 && (
            <span className="font-mono text-[10px] text-teal-400 bg-teal-500/10 px-2 py-1 border border-teal-500/30">
              BALANCE ${wallet.balance.toFixed(2)}
            </span>
          )}
          <Link to="/my-consignments" className="flex items-center gap-1 text-xs text-teal-400 font-mono">
            <Plus size={14} /> CONSIGN
          </Link>
        </div>
      </div>

      {items.length === 0 ? (
        <div className="text-center py-6">
          <Handshake className="mx-auto mb-2 text-zinc-600" size={28} />
          <p className="text-zinc-500 text-xs mb-2 font-mono">NO CONSIGNMENT ITEMS YET</p>
          <Link to="/my-consignments" className="inline-block px-3 py-1.5 bg-teal-500 text-black text-xs font-bold font-mono">
            CONSIGN AN ITEM
          </Link>
        </div>
      ) : (
        <div className="space-y-2">
          {items.map(item => {
            const open = expanded === item.id;
            const photos = item.photos || [];
            return (
              <div key={item.id} className="border border-zinc-700 rounded-none overflow-hidden" data-testid={`my-cons-${item.id}`}>
                {/* Header (clickable) */}
                <div
                  className="p-2 flex items-center justify-between cursor-pointer hover:bg-zinc-900/50"
                  onClick={() => setExpanded(open ? null : item.id)}
                  data-testid={`my-cons-toggle-${item.id}`}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <div className="p-1.5 rounded-none bg-teal-500/20 text-teal-400 shrink-0">
                      <Handshake size={14} />
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs font-mono uppercase truncate">{item.title}</p>
                      <p className="text-[10px] text-zinc-500 font-mono">
                        SUGG ${item.suggested_price?.toFixed(2)}
                        {item.final_price ? ` · LIST $${item.final_price.toFixed(2)}` : ''}
                        {item.sold_price ? ` · SOLD $${item.sold_price.toFixed(2)}` : ''}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {renderStatusBadge(item.status)}
                    {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                  </div>
                </div>

                {/* Expanded */}
                {open && (
                  <div className="border-t border-zinc-700 p-2 space-y-2">
                    {item.description && (
                      <div className="bg-zinc-900 p-2">
                        <p className="text-[10px] text-zinc-500 font-mono mb-1">DESCRIPTION</p>
                        <p className="text-xs font-mono text-white uppercase">{item.description}</p>
                      </div>
                    )}

                    <div className="grid grid-cols-3 gap-1 text-center text-xs font-mono">
                      <div className="bg-zinc-800 p-1.5">
                        <p className="text-zinc-500">SUGG</p>
                        <p>${(item.suggested_price ?? 0).toFixed(2)}</p>
                      </div>
                      <div className="bg-zinc-800 p-1.5">
                        <p className="text-zinc-500">LIST</p>
                        <p className="text-teal-400">{item.final_price ? `$${item.final_price.toFixed(2)}` : '—'}</p>
                      </div>
                      <div className="bg-zinc-800 p-1.5">
                        <p className="text-zinc-500">EARNED</p>
                        <p className="text-purple-400">{item.seller_earnings ? `+$${item.seller_earnings.toFixed(2)}` : '—'}</p>
                      </div>
                    </div>

                    {photos.length > 0 && (
                      <div className="bg-zinc-900 p-2">
                        <p className="text-[10px] text-zinc-500 font-mono mb-1">PHOTOS</p>
                        <div className="flex gap-1 flex-wrap">
                          {photos.map((p, idx) => (
                            <img
                              key={idx}
                              src={getImageUrl(p)}
                              alt=""
                              className="w-12 h-12 object-cover border border-zinc-800 cursor-zoom-in hover:opacity-80"
                              loading="lazy"
                              onClick={(e) => { e.preventDefault(); e.stopPropagation(); setLightbox({ images: photos, initialIndex: idx }); }}
                              data-testid={`cons-photo-${item.id}-${idx}`}
                            />
                          ))}
                        </div>
                      </div>
                    )}

                    {item.admin_notes && (
                      <div className="bg-zinc-900 border-l-2 border-teal-500/50 p-2">
                        <p className="text-[10px] text-zinc-500 font-mono mb-1">NOTE FROM BLK MRKT</p>
                        <p className="text-xs font-mono text-zinc-300 italic">"{item.admin_notes}"</p>
                      </div>
                    )}

                    {item.status === 'listed' && item.expires_at && (
                      <p className="text-[10px] text-zinc-500 font-mono">REASSESS BY {new Date(item.expires_at).toLocaleDateString()}</p>
                    )}

                    <div className="flex gap-2 pt-1">
                      <Link
                        to="/my-consignments"
                        className="flex-1 text-center px-2 py-1.5 border border-teal-500/40 text-teal-400 hover:bg-teal-500/10 font-mono text-[10px] tracking-widest"
                        data-testid={`open-full-cons-${item.id}`}
                      >
                        OPEN FULL VIEW
                      </Link>
                      {wallet.balance > 0 && (
                        <Link
                          to="/my-consignments"
                          className="flex-1 text-center px-2 py-1.5 border border-purple-500/40 text-purple-400 hover:bg-purple-500/10 font-mono text-[10px] tracking-widest flex items-center justify-center gap-1"
                        >
                          <Send size={10} /> PAYOUT
                        </Link>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      {lightbox && (
        <ImageLightbox
          images={lightbox.images}
          initialIndex={lightbox.initialIndex}
          onClose={() => setLightbox(null)}
        />
      )}
    </div>
  );
}
